import { useStore } from '@hooks/useStore'
import { CheckCircle, Error as ErrorIcon, Email } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import { IGmailOAuthStatus } from '@typings'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

export const GmailOAuthConnect = observer(() => {
  const { api } = useStore()
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<IGmailOAuthStatus | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fetchStatus = async () => {
    setLoading(true)
    setError(null)
    try {
      const gmailStatus = await api.oauth.getGmailStatus()
      setStatus(gmailStatus)
    } catch (err: any) {
      console.error('Error fetching Gmail status:', err)
      setStatus({ connected: false })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStatus()
  }, [])

  const handleConnect = async () => {
    setLoading(true)
    setError(null)
    try {
      const authUrl = await api.oauth.getGmailAuthUrl()
      // Open OAuth URL in new window
      window.open(authUrl, '_blank', 'width=600,height=700')

      // Poll for connection status every 2 seconds
      const pollInterval = setInterval(async () => {
        const gmailStatus = await api.oauth.getGmailStatus()
        if (gmailStatus.connected) {
          setStatus(gmailStatus)
          setLoading(false)
          clearInterval(pollInterval)
        }
      }, 2000)

      // Stop polling after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval)
        setLoading(false)
      }, 300000)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Verbinding mislukt')
      setLoading(false)
    }
  }

  const handleDisconnect = async () => {
    if (!window.confirm('Weet je zeker dat je Gmail wilt ontkoppelen?')) {
      return
    }

    setLoading(true)
    setError(null)
    try {
      await api.oauth.disconnectGmail()
      setStatus({ connected: false })
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Ontkoppelen mislukt')
    } finally {
      setLoading(false)
    }
  }

  if (loading && !status) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle1" fontWeight="medium">
                Gmail Verbinding
              </Typography>
              {status?.connected && status.email && (
                <Typography variant="body2" color="text.secondary">
                  {status.email}
                </Typography>
              )}
            </Box>
            <Chip
              label={status?.connected ? 'Verbonden' : 'Niet verbonden'}
              color={status?.connected ? 'success' : 'default'}
              size="small"
              icon={status?.connected ? <CheckCircle /> : <Email />}
            />
          </Stack>

          {error && (
            <Alert severity="error" icon={<ErrorIcon />}>
              {error}
            </Alert>
          )}

          {status?.connected ? (
            <Stack spacing={1}>
              {status.needs_refresh && (
                <Alert severity="warning">
                  Token verloopt binnenkort. Deze wordt automatisch vernieuwd.
                </Alert>
              )}
              <Button
                variant="outlined"
                color="error"
                onClick={handleDisconnect}
                disabled={loading}
                fullWidth>
                {loading ? 'Ontkoppelen...' : 'Ontkoppel Gmail'}
              </Button>
            </Stack>
          ) : (
            <Button
              variant="contained"
              onClick={handleConnect}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Email />}
              fullWidth>
              {loading ? 'Verbinden...' : 'Verbind met Gmail'}
            </Button>
          )}

          {!status?.connected && (
            <Typography variant="caption" color="text.secondary">
              Door te verbinden met Gmail kun je facturen en offertes versturen vanaf je eigen Gmail-account.
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
})
